import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useStripeSubscription } from './useStripeSubscription';
import type { StripeSubscriptionSummary } from '../lib/stripeBilling';

/** How the user's access stands right now. */
export type TrialPhase =
  | 'loading'         // still resolving auth / subscription
  | 'active'          // within trial window OR active paid subscription
  | 'warning'         // trial ends in ≤ WARNING_THRESHOLD_DAYS days
  | 'expired_grace'   // trial/subscription ended but within 30-day grace window
  | 'expired';        // grace period over — data has been / should be deleted

export interface TrialStatus {
  phase: TrialPhase;
  /** Days remaining in the trial (0 once expired). */
  trialDaysLeft: number;
  /** Days remaining in the grace period (0 once fully expired). */
  graceDaysLeft: number;
  hasActiveSubscription: boolean;
  /** The best active subscription (null when none or loading). */
  subscription: StripeSubscriptionSummary | null;
  /** Date the trial / subscription period ends. */
  trialEndDate: Date | null;
  /** Date after which data will be permanently deleted. */
  graceEndDate: Date | null;
}

const TRIAL_DAYS = 15;
const GRACE_DAYS = 30;
const WARNING_THRESHOLD_DAYS = 3;
const MS_PER_DAY = 1_000 * 60 * 60 * 24;

export function useTrialStatus(): TrialStatus {
  const { firebaseUser, user } = useAuth();
  const { subscription, loading: subLoading } = useStripeSubscription(user?.id);
  return useMemo(() => {
    const loading: TrialStatus = {
      phase: 'loading',
      trialDaysLeft: 0,
      graceDaysLeft: 0,
      hasActiveSubscription: false,
      subscription: null,
      trialEndDate: null,
      graceEndDate: null,
    };

    if (!firebaseUser || subLoading) return loading;

    // ── Active subscription (includes trial periods from Stripe and Lemon Squeezy) ──
    const activeStatuses = new Set(['active', 'trialing', 'on_trial']);
    const hasActiveSub = Boolean(
      subscription?.status && activeStatuses.has(subscription.status),
    );

    if (hasActiveSub) {
      return {
        phase: 'active',
        trialDaysLeft: 0,
        graceDaysLeft: 0,
        hasActiveSubscription: true,
        subscription,
        trialEndDate: null,
        graceEndDate: null,
      };
    }

    // ── Work out the access expiry date ──────────────────────────────────────
    const createdAt = firebaseUser.metadata.creationTime
      ? new Date(firebaseUser.metadata.creationTime)
      : new Date();

    const starterTrialEnd = new Date(createdAt.getTime() + TRIAL_DAYS * MS_PER_DAY);

    // If they had a paid subscription that is now cancelled, use the period end
    // as the expiry so they get access until the billing period runs out.
    let expiryDate = starterTrialEnd;
    if (subscription?.nextPaymentAt) {
      const periodEnd = new Date(subscription.nextPaymentAt);
      if (!Number.isNaN(periodEnd.getTime()) && periodEnd > starterTrialEnd) {
        expiryDate = periodEnd;
      }
    }

    // ── Grace period — resets on each login (user-friendly) ──────────────────
    const lastLogin = firebaseUser.metadata.lastSignInTime
      ? new Date(firebaseUser.metadata.lastSignInTime)
      : null;

    // Grace starts from whichever is later: expiry date or last login
    const graceStart =
      lastLogin && lastLogin > expiryDate ? lastLogin : expiryDate;
    const graceEndDate = new Date(graceStart.getTime() + GRACE_DAYS * MS_PER_DAY);

    // ── Calculate days remaining ──────────────────────────────────────────────
    const now = new Date();
    const trialDaysLeft = Math.max(
      0,
      Math.ceil((expiryDate.getTime() - now.getTime()) / MS_PER_DAY),
    );
    const graceDaysLeft = Math.max(
      0,
      Math.ceil((graceEndDate.getTime() - now.getTime()) / MS_PER_DAY),
    );

    // ── Determine phase ───────────────────────────────────────────────────────
    let phase: TrialPhase;
    if (trialDaysLeft > WARNING_THRESHOLD_DAYS) {
      phase = 'active';
    } else if (trialDaysLeft > 0) {
      phase = 'warning';
    } else if (graceDaysLeft > 0) {
      phase = 'expired_grace';
    } else {
      phase = 'expired';
    }

    return {
      phase,
      trialDaysLeft,
      graceDaysLeft,
      hasActiveSubscription: false,
      subscription,
      trialEndDate: expiryDate,
      graceEndDate,
    };
  }, [firebaseUser, subscription, subLoading]);
}
